import { useContext, useCallback, Dispatch, SetStateAction, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationAdminDashboard } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { CreateDealClassifierDto } from '@dealstack/shared/lib/generated';
import { Classifier } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/types';
import { createClassifierResolver } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/CreateClassifierDrawer/validationSchema';
import { DealContext } from '@dealstack/frontend/app/pages/Project/DealDashboard/DealContext';
import { buildCreateData } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/utils';
import { isEmpty } from 'lodash';
import { useCreateClassifier } from '@dealstack/frontend/app/pages/Project/DealSettings/Classifiers/hooks/useCreateClassifier';

export const useCreateClassifierDrawer = (
	classifier: Classifier,
	setShowCreateUpdateClassificationDrawer: Dispatch<SetStateAction<boolean>>
) => {
	const tAdminDashboard = useTranslationAdminDashboard();
	const { dealId } = useContext(DealContext);
	const { createClassifier, isLoading } = useCreateClassifier(dealId || '');

	const formState = useForm<CreateDealClassifierDto>({
		resolver: createClassifierResolver,
		defaultValues: {
			name: classifier.name,
			classifierParentId: null,
		},
	});

	// reset form when classifier prop changes
	useEffect(() => {
		formState.reset({
			name: classifier.name,
			classifierParentId: null,
		});
	}, [classifier, formState]);

	const onSave = useCallback(async () => {
		const formValues = formState.getValues();

		if (isEmpty(formValues.name)) {
			showToastV2({
				title: tAdminDashboard('classifier.errors.empty-field'),
				state: 'error',
			});
			return;
		}

		if (!dealId) {
			showToastV2({
				title: tAdminDashboard('classifier.errors.no-deal-id'),
				state: 'error',
			});
			return;
		}

		const createClassifierData = buildCreateData({ formValues, dealId });

		void createClassifier(createClassifierData);
		setShowCreateUpdateClassificationDrawer(false);
	}, [
		formState,
		dealId,
		createClassifier,
		tAdminDashboard,
		setShowCreateUpdateClassificationDrawer,
	]);

	return {
		formState,
		isLoading,
		onSave,
	};
};
